import React, { useState } from 'react'
import { Button, Card, Col, Input, Row, Select, Table, Typography, Tag, Modal } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash, faEye, faFileAlt, faVideo, faBook } from '@fortawesome/free-solid-svg-icons'
import mealEducationResourcesData from '../utils/mealEducationResources.json'

const { Title, Text, Paragraph } = Typography
const { Option } = Select
const { TextArea } = Input

const MealEducation = ({ data, setData }) => {
  const [selectedResourceId, setSelectedResourceId] = useState('')
  const [resourceNotes, setResourceNotes] = useState('')
  const [resourceModalVisible, setResourceModalVisible] = useState(false)
  const [selectedResourceDetails, setSelectedResourceDetails] = useState(null)

  const resources = mealEducationResourcesData.resources
  const resourceTypes = mealEducationResourcesData.resourceTypes

  const handleAddResource = () => {
    if (selectedResourceId) {
      const resourceToAdd = resources.find((r) => r.id === selectedResourceId)

      if (resourceToAdd && !data.some((r) => r.id === selectedResourceId)) {
        setData([
          ...data,
          {
            ...resourceToAdd,
            notes: resourceNotes,
            assigned: new Date().toISOString().split('T')[0]
          }
        ])

        // Reset form
        setSelectedResourceId('')
        setResourceNotes('')
      }
    }
  }

  const handleRemoveResource = (id) => {
    setData(data.filter((r) => r.id !== id))
  }

  const viewResourceDetails = (resource) => {
    setSelectedResourceDetails(resource)
    setResourceModalVisible(true)
  }

  // Get icon based on resource type
  const getResourceIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return <FontAwesomeIcon icon={faFileAlt} />
      case 'video':
        return <FontAwesomeIcon icon={faVideo} />
      case 'book':
        return <FontAwesomeIcon icon={faBook} />
      default:
        return <FontAwesomeIcon icon={faFileAlt} />
    }
  }

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (text) => (
        <span>
          {getResourceIcon(text)} {text}
        </span>
      )
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => <a onClick={() => viewResourceDetails(record)}>{text}</a>
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => <Tag color='blue'>{category}</Tag>
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true
    },
    {
      title: 'Assigned',
      dataIndex: 'assigned',
      key: 'assigned'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          danger
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => handleRemoveResource(record.id)}
          size='small'
        />
      )
    }
  ]

  return (
    <div>
      <Title level={4}>Educational Resources & Guidance</Title>
      <Text>Provide educational materials and resources to support the meal plan implementation.</Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Text strong>Select Resource</Text>
            <Select
              showSearch
              placeholder='Select educational resource'
              style={{ width: '100%' }}
              value={selectedResourceId}
              onChange={(value) => setSelectedResourceId(value)}
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {resources.map((resource) => (
                <Option key={resource.id} value={resource.id}>
                  {resource.title} ({resource.type}) - {resource.category}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <Text strong>Additional Notes</Text>
            <TextArea
              rows={4}
              placeholder='Enter notes or instructions for this resource'
              value={resourceNotes}
              onChange={(e) => setResourceNotes(e.target.value)}
            />
          </Col>

          <Col span={24}>
            <Button
              type='primary'
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddResource}
              disabled={!selectedResourceId}
            >
              Add Educational Resource
            </Button>
          </Col>
        </Row>
      </Card>

      <Table dataSource={data} columns={columns} rowKey='id' pagination={false} size='small' />

      <Modal
        title={selectedResourceDetails?.title}
        open={resourceModalVisible}
        onCancel={() => setResourceModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedResourceDetails && (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <p>
                    <strong>Type:</strong> {selectedResourceDetails.type}
                  </p>
                  <p>
                    <strong>Category:</strong> {selectedResourceDetails.category}
                  </p>
                  <p>
                    <strong>Author:</strong> {selectedResourceDetails.author}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Published:</strong> {selectedResourceDetails.publishedDate}
                  </p>
                  <p>
                    <strong>Duration:</strong> {selectedResourceDetails.duration || 'N/A'}
                  </p>
                  <p>
                    <strong>Language:</strong> {selectedResourceDetails.language || 'English'}
                  </p>
                </Col>
              </Row>
            </Card>

            <Paragraph strong>Description:</Paragraph>
            <Paragraph>{selectedResourceDetails.description}</Paragraph>

            <Paragraph strong>Key Topics:</Paragraph>
            <ul>
              {selectedResourceDetails.topics.map((topic, index) => (
                <li key={index}>{topic}</li>
              ))}
            </ul>

            {selectedResourceDetails.url && (
              <div style={{ marginTop: 16 }}>
                <Button
                  type='primary'
                  icon={<FontAwesomeIcon icon={faEye} />}
                  onClick={() => window.open(selectedResourceDetails.url, '_blank')}
                >
                  View Resource
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

export default MealEducation
